import { useState, useEffect } from 'react';
import { configService } from '@/lib/config-service';
import { useAuth } from '@/providers/AuthProvider';
import { useCompany } from '@/contexts/CompanyContext';
import { useDemoSession } from '@/providers/DemoSessionProvider';

/**
 * Custom hook to build the feedback collector URL with all required parameters.
 *
 * @param feature - The feature name to collect feedback for (e.g., "Dashboard", "Cost Centres")
 * @returns The complete URL for the oc-component, or null if config is not loaded yet
 */
export function useFeedbackCollectorUrl(feature: string): string | null {
  const [url, setUrl] = useState<string | null>(null);
  const { authenticatedUser, isProduction } = useAuth();
  const { currentCompany } = useCompany();
  const { userName } = useDemoSession();

  useEffect(() => {
    let isMounted = true;

    async function buildUrl() {
      try {
        const config = await configService.getFeedbackCollectorConfig();

        if (!isMounted) return;

        const params = new URLSearchParams();
        params.set('product', config.product);
        params.set('feature', feature);
        params.set('accountToken', config.accountToken);
        params.set('customerName', currentCompany?.name || 'Unknown');
        const userId = isProduction
          ? authenticatedUser?.email || 'Unknown'
          : userName || 'Demo User';
        params.set('userId', userId);

        const fullUrl = `${config.baseUrl}?${params.toString()}`;
        setUrl(fullUrl);
      } catch (error) {
        console.error('Failed to build feedback collector URL:', error);
        setUrl(null);
      }
    }

    buildUrl();

    return () => {
      isMounted = false;
    };
  }, [feature, authenticatedUser, currentCompany, isProduction, userName]);

  return url;
}
